import React, { useEffect, useRef, useState } from 'react'
import { HiSearch } from 'react-icons/hi'
import { Badge, Label, Spinner, TextInput } from 'flowbite-react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { API_BASE } from '../constants/inviteCode'
import { toastApiError } from '../utils/apiError'
import { deleteAuthorityWithLeads } from '../utils/authorityDelete'
import TrashIconButton from './TrashIconButton'

const DISPLAY_FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'county', label: 'County' },
  { key: 'postcode', label: 'Postcode' },
  { key: 'official_domain', label: 'Domain' },
  { key: 'onboarded', label: 'Onboarded' },
]

const parseAuthorityList = (data) => {
  const items = Array.isArray(data) ? data : data?.items || []
  return items.slice(0, 20)
}

const formatCellValue = (key, value) => {
  if (key === 'onboarded') {
    if (value === true) {
      return <Badge color="success">Yes</Badge>
    }
    if (value === false) {
      return <Badge color="gray">No</Badge>
    }
    return '—'
  }
  if (value === null || value === undefined || value === '') {
    return '—'
  }
  return value
}

const AuthorityAutocomplete = ({
  token,
  onSelect,
  placeholder = 'Search cities by name, location, or domain...',
  autoFocus = false,
  limit = 20,
  onboardedOnly = false,
}) => {
  const [query, setQuery] = useState('')
  const [defaultResults, setDefaultResults] = useState([])
  const [searchResults, setSearchResults] = useState([])
  const [initialLoading, setInitialLoading] = useState(true)
  const [searchLoading, setSearchLoading] = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const debounceRef = useRef(null)

  const cancelPendingSearch = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
    setSearchLoading(false)
  }

  const trimmedQuery = query.trim()
  const isSearching = trimmedQuery.length >= 1
  const results = isSearching ? searchResults : defaultResults
  const loading = isSearching ? searchLoading : initialLoading

  const loadDefaultList = async () => {
    setInitialLoading(true)
    try {
      const params = {
        limit: 20,
        skip: 0,
      }
      if (onboardedOnly) {
        params.onboarded_only = true
      }

      const response = await axios.get(`${API_BASE}/authorities`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      })
      setDefaultResults(parseAuthorityList(response.data))
    } catch (error) {
      toastApiError(error, 'Failed to load cities')
      setDefaultResults([])
    } finally {
      setInitialLoading(false)
    }
  }

  useEffect(() => {
    loadDefaultList()
    // loadDefaultList reads only token/onboardedOnly, both listed. It is
    // recreated every render, so listing it would refetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, onboardedOnly])

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    if (!isSearching) {
      setSearchResults([])
      setSearchLoading(false)
      return
    }

    setSearchLoading(true)
    debounceRef.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          q: trimmedQuery,
          limit: String(limit),
        })
        if (onboardedOnly) {
          params.set('onboarded_only', 'true')
        }

        const response = await axios.get(
          `${API_BASE}/authorities/autocomplete?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
        const items = Array.isArray(response.data) ? response.data : []
        setSearchResults(items)
      } catch (error) {
        toastApiError(error, 'Failed to search cities')
        setSearchResults([])
      } finally {
        setSearchLoading(false)
      }
    }, 300)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [query, token, limit, onboardedOnly, isSearching, trimmedQuery])

  const removeFromResults = (authorityId) => {
    setDefaultResults((current) => current.filter((item) => item.id !== authorityId))
    setSearchResults((current) => current.filter((item) => item.id !== authorityId))
  }

  const handleRowSelect = (item) => {
    cancelPendingSearch()
    onSelect?.(item)
  }

  const handleDelete = async (item, event) => {
    event.stopPropagation()
    if (deletingId) return

    setDeletingId(item.id)
    try {
      const { deleted, cancelled } = await deleteAuthorityWithLeads({
        token,
        authorityId: item.id,
        domain: item.official_domain,
        name: item.name,
      })
      if (cancelled) return
      if (deleted) {
        removeFromResults(item.id)
        toast.success(`Deleted ${item.name}`)
      }
    } catch (error) {
      toastApiError(error, 'Failed to delete city')
    } finally {
      setDeletingId(null)
    }
  }

  const emptyMessage = isSearching
    ? `No cities matched "${trimmedQuery}".`
    : 'No cities found.'

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="city-search">Search Cities</Label>
        <div className="relative mt-1">
          <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <TextInput
            id="city-search"
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={placeholder}
            autoFocus={autoFocus}
            className="pl-10"
            autoComplete="off"
          />
          {loading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <Spinner size="sm" />
            </div>
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700/60">
              <tr>
                {DISPLAY_FIELDS.map((field) => (
                  <th
                    key={field.key}
                    className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                  >
                    {field.label}
                  </th>
                ))}
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  <span className="sr-only">Delete</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
              {loading ? (
                <tr>
                  <td
                    colSpan={DISPLAY_FIELDS.length + 1}
                    className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                  >
                    <span className="inline-flex items-center gap-2">
                      <Spinner size="sm" />
                      {isSearching ? 'Searching cities...' : 'Loading cities...'}
                    </span>
                  </td>
                </tr>
              ) : results.length === 0 ? (
                <tr>
                  <td
                    colSpan={DISPLAY_FIELDS.length + 1}
                    className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                  >
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                results.map((item) => (
                  <tr
                    key={item.id}
                    onClick={() => handleRowSelect(item)}
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40"
                  >
                    {DISPLAY_FIELDS.map((field) => (
                      <td
                        key={field.key}
                        className={`px-4 py-3 text-sm text-gray-700 dark:text-gray-300 ${
                          field.key === 'official_domain' ? 'font-mono text-gray-600 dark:text-gray-400' : ''
                        }`}
                      >
                        {formatCellValue(field.key, item[field.key])}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right">
                      <TrashIconButton
                        label={`Delete ${item.name}`}
                        loading={deletingId === item.id}
                        disabled={Boolean(deletingId && deletingId !== item.id)}
                        onClick={(event) => handleDelete(item, event)}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {!loading && results.length > 0 && (
          <div className="border-t border-gray-200 bg-gray-50 px-4 py-2 text-xs text-gray-500 dark:border-gray-700 dark:bg-gray-800/80 dark:text-gray-400">
            {results.length} cit{results.length === 1 ? 'y' : 'ies'}
            {isSearching ? ` matching "${trimmedQuery}"` : ''}
            {' · '}Click a row to open its profile
          </div>
        )}
      </div>
    </div>
  )
}

export default AuthorityAutocomplete
